var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { PanelLeft } from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile.js';
import { cn } from '../../lib/utils.js';
import { Button } from './button.js';
import { Input } from './input.js';
import { Separator } from './separator.js';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, } from './sheet.js';
import { Skeleton } from './skeleton.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip.js';
var SIDEBAR_COOKIE_NAME = 'sidebar_state';
var SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
var SIDEBAR_WIDTH_STORAGE_KEY = 'sidebar_width';
var SIDEBAR_PREFERRED_WIDTH_KEY = 'sidebar_preferred_width';
var SIDEBAR_MIN_WIDTH = 240;
var SIDEBAR_MAX_WIDTH = 400;
var SIDEBAR_DEFAULT_WIDTH = 280;
var SIDEBAR_COLLAPSE_THRESHOLD = 220;
var SIDEBAR_WIDTH_ICON = '3rem';
var SIDEBAR_KEYBOARD_SHORTCUT = 'b';
function mergeRefs() {
    var refs = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        refs[_i] = arguments[_i];
    }
    return function (node) {
        refs.forEach(function (ref) {
            if (!ref)
                return;
            if (typeof ref === 'function') {
                ref(node);
            }
            else {
                // Allow both `T` and `null` to be assigned to `current`
                ref.current = node;
            }
        });
    };
}
var SidebarContext = React.createContext(null);
function useSidebar() {
    var ctx = React.useContext(SidebarContext);
    if (!ctx) {
        throw new Error('useSidebar must be used within a SidebarProvider.');
    }
    return ctx;
}
var SidebarProvider = React.forwardRef(function SidebarProvider(_a, forwardedRef) {
    var _b = _a.defaultOpen, defaultOpen = _b === void 0 ? true : _b, openProp = _a.open, setOpenProp = _a.onOpenChange, className = _a.className, style = _a.style, children = _a.children, props = __rest(_a, ["defaultOpen", "open", "onOpenChange", "className", "style", "children"]);
    var isMobile = useIsMobile();
    var wrapperRef = React.useRef(null);
    var _c = React.useState(false), openMobile = _c[0], setOpenMobile = _c[1];
    var _d = React.useState(false), isResizing = _d[0], setIsResizing = _d[1];
    var _e = React.useState(function () {
        try {
            var preferred = localStorage.getItem(SIDEBAR_PREFERRED_WIDTH_KEY);
            if (preferred) {
                var w = parseInt(preferred, 10);
                if (w >= SIDEBAR_DEFAULT_WIDTH && w <= SIDEBAR_MAX_WIDTH) {
                    return w;
                }
            }
            var saved = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
            if (saved) {
                var w = parseInt(saved, 10);
                if (w >= SIDEBAR_DEFAULT_WIDTH && w <= SIDEBAR_MAX_WIDTH) {
                    return w;
                }
            }
        }
        catch (_a) { }
        return SIDEBAR_DEFAULT_WIDTH;
    }), width = _e[0], setWidthState = _e[1];
    var _f = React.useState(defaultOpen), _open = _f[0], _setOpen = _f[1];
    var open = openProp !== null && openProp !== void 0 ? openProp : _open;
    var setOpen = React.useCallback(function (val) {
        var next = typeof val === 'function' ? val(open) : val;
        if (setOpenProp)
            setOpenProp(next);
        else
            _setOpen(next);
        document.cookie = "".concat(SIDEBAR_COOKIE_NAME, "=").concat(next, "; path=/; max-age=").concat(SIDEBAR_COOKIE_MAX_AGE);
    }, [open, setOpenProp]);
    var setWidth = React.useCallback(function (newWidth) {
        var constrained = Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, newWidth));
        if (constrained < SIDEBAR_COLLAPSE_THRESHOLD && open) {
            setOpen(false);
            return;
        }
        else if (constrained >= SIDEBAR_COLLAPSE_THRESHOLD && !open) {
            setOpen(true);
        }
        setWidthState(constrained);
        try {
            localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, "".concat(constrained));
            if (constrained > SIDEBAR_DEFAULT_WIDTH) {
                localStorage.setItem(SIDEBAR_PREFERRED_WIDTH_KEY, "".concat(constrained));
            }
            else {
                localStorage.removeItem(SIDEBAR_PREFERRED_WIDTH_KEY);
            }
        }
        catch (_a) { }
    }, [open, setOpen]);
    React.useEffect(function () {
        var body = document.body;
        if (isResizing)
            body.classList.add('sidebar-resizing');
        else
            body.classList.remove('sidebar-resizing');
    }, [isResizing]);
    React.useEffect(function () {
        if (!open)
            return;
        setTimeout(function () {
            var _a;
            try {
                var pref = localStorage.getItem(SIDEBAR_PREFERRED_WIDTH_KEY);
                if (pref) {
                    var w = parseInt(pref, 10);
                    if (w !== width) {
                        setWidthState(w);
                        localStorage.removeItem(SIDEBAR_PREFERRED_WIDTH_KEY);
                    }
                }
            }
            catch (_b) { }
            var content = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('[data-sidebar="content"]');
            if (content && content.scrollWidth > content.clientWidth) {
                setWidth(SIDEBAR_MAX_WIDTH);
            }
        }, 100);
    }, [open, setWidth, width]);
    var toggleSidebar = React.useCallback(function () {
        return isMobile ? setOpenMobile(function (o) { return !o; }) : setOpen(function (o) { return !o; });
    }, [isMobile, setOpen]);
    React.useEffect(function () {
        var onKey = function (e) {
            if (e.key === SIDEBAR_KEYBOARD_SHORTCUT && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                toggleSidebar();
            }
        };
        window.addEventListener('keydown', onKey);
        return function () { return window.removeEventListener('keydown', onKey); };
    }, [toggleSidebar]);
    var state = open ? 'expanded' : 'collapsed';
    var contextValue = React.useMemo(function () { return ({
        state: state,
        open: open,
        setOpen: setOpen,
        isMobile: isMobile,
        openMobile: openMobile,
        setOpenMobile: setOpenMobile,
        toggleSidebar: toggleSidebar,
        width: width,
        setWidth: setWidth,
        isResizing: isResizing,
        setIsResizing: setIsResizing,
        wrapperRef: wrapperRef,
    }); }, [
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        width,
        setWidth,
        isResizing,
    ]);
    return (_jsx(SidebarContext.Provider, { value: contextValue, children: _jsx(TooltipProvider, { delayDuration: 0, children: _jsx("div", __assign({ ref: mergeRefs(wrapperRef, forwardedRef), style: __assign({ '--sidebar-width': "".concat(width, "px"), '--sidebar-width-icon': SIDEBAR_WIDTH_ICON }, style), className: cn('group/sidebar-wrapper flex min-h-svh w-full has-[[data-variant=inset]]:bg-sidebar', className) }, props, { children: children })) }) }));
});
SidebarProvider.displayName = 'SidebarProvider';
var Sidebar = React.forwardRef(function Sidebar(_a, ref) {
    var _b = _a.side, side = _b === void 0 ? 'left' : _b, _c = _a.variant, variant = _c === void 0 ? 'sidebar' : _c, _d = _a.collapsible, collapsible = _d === void 0 ? 'offcanvas' : _d, className = _a.className, children = _a.children, props = __rest(_a, ["side", "variant", "collapsible", "className", "children"]);
    var _e = useSidebar(), isMobile = _e.isMobile, state = _e.state, openMobile = _e.openMobile, setOpenMobile = _e.setOpenMobile, isResizing = _e.isResizing;
    if (collapsible === 'none') {
        return (_jsxs("div", __assign({ className: cn('flex h-full w-[--sidebar-width] flex-col bg-sidebar text-sidebar-foreground', className), ref: ref }, props, { children: [children, _jsx(SidebarResizeHandle, { side: side })] })));
    }
    if (isMobile) {
        return (_jsx(Sheet, __assign({ open: openMobile, onOpenChange: setOpenMobile }, props, { children: _jsxs(SheetContent, { "data-sidebar": "sidebar", "data-mobile": "true", className: "w-[--sidebar-width] bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden", style: {
                    '--sidebar-width': SIDEBAR_WIDTH_ICON,
                }, side: side, children: [_jsxs(SheetHeader, { className: "sr-only", children: [_jsx(SheetTitle, { children: "Sidebar" }), _jsx(SheetDescription, { children: "Displays the mobile sidebar." })] }), _jsx("div", { className: "flex h-full w-full flex-col", children: children })] }) })));
    }
    return (_jsxs("div", { ref: ref, className: "group peer hidden text-sidebar-foreground md:block", "data-state": state, "data-collapsible": state === 'collapsed' ? collapsible : '', "data-variant": variant, "data-side": side, children: [_jsx("div", { className: cn('relative w-[--sidebar-width] bg-transparent ease-linear', !isResizing && 'transition-[width] duration-200', 'group-data-[collapsible=offcanvas]:w-0', 'group-data-[side=right]:rotate-180', variant === 'floating' || variant === 'inset'
                    ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]'
                    : 'group-data-[collapsible=icon]:w-[--sidebar-width-icon]') }), _jsx("div", __assign({ className: cn('fixed inset-y-0 z-10 hidden h-svh w-[--sidebar-width] ease-linear md:flex', !isResizing && 'transition-[left,right,width] duration-200', side === 'left'
                    ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
                    : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]', variant === 'floating' || variant === 'inset'
                    ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]'
                    : 'group-data-[collapsible=icon]:w-[--sidebar-width-icon] group-data-[side=left]:border-r group-data-[side=right]:border-l', className) }, props, { children: _jsxs("div", { "data-sidebar": "sidebar", className: "flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow relative", children: [children, _jsx(SidebarResizeHandle, { side: side })] }) }))] }));
});
Sidebar.displayName = 'Sidebar';
var SidebarResizeHandle = React.forwardRef(function SidebarResizeHandle(_a, ref) {
    var className = _a.className, _b = _a.side, side = _b === void 0 ? 'left' : _b, props = __rest(_a, ["className", "side"]);
    var _c = useSidebar(), wrapperRef = _c.wrapperRef, setWidth = _c.setWidth, isResizing = _c.isResizing, setIsResizing = _c.setIsResizing, state = _c.state;
    if (state === 'collapsed')
        return null;
    var handlePointerDown = function (e) {
        e.preventDefault();
        var container = wrapperRef.current;
        if (!container)
            return;
        var rect = container.getBoundingClientRect();
        var startLeft = rect.left;
        var startRight = rect.right;
        container.setPointerCapture(e.pointerId);
        setIsResizing(true);
        document.body.style.cursor = 'col-resize';
        var onPointerMove = function (ev) {
            ev.preventDefault();
            var raw = side === 'left' ? ev.clientX - startLeft : startRight - ev.clientX;
            var constrained = Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, raw));
            container.style.setProperty('--sidebar-width', "".concat(constrained, "px"));
        };
        var onPointerUp = function (ev) {
            container.releasePointerCapture(e.pointerId);
            document.removeEventListener('pointermove', onPointerMove);
            document.removeEventListener('pointerup', onPointerUp);
            var final = parseInt(container.style.getPropertyValue('--sidebar-width'), 10);
            setWidth(final);
            setIsResizing(false);
            document.body.style.cursor = '';
        };
        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
    };
    return (_jsxs("div", __assign({ ref: ref, onPointerDown: handlePointerDown, className: cn('absolute top-0 bottom-0 w-2 opacity-0 transition-opacity z-50 cursor-col-resize pointer-events-none group-hover/sidebar-wrapper:pointer-events-auto', 'group-hover/sidebar-wrapper:opacity-100', side === 'left' ? 'right-[-2px]' : 'left-[-2px]', className) }, props, { children: [_jsx("div", { className: cn('absolute inset-y-0 w-1 transition-colors', side === 'left' ? 'right-1' : 'left-1', isResizing && 'bg-primary/40') }), _jsx("div", { className: "absolute inset-0" })] })));
});
SidebarResizeHandle.displayName = 'SidebarResizeHandle';
var SidebarTrigger = React.forwardRef(function SidebarTrigger(_a, ref) {
    var className = _a.className, onClick = _a.onClick, props = __rest(_a, ["className", "onClick"]);
    var toggleSidebar = useSidebar().toggleSidebar;
    return (_jsxs(Button, __assign({ ref: ref, "data-sidebar": "trigger", variant: "ghost", size: "icon", className: cn('h-7 w-7', className), onClick: function (event) {
            onClick === null || onClick === void 0 ? void 0 : onClick(event);
            toggleSidebar();
        } }, props, { children: [_jsx(PanelLeft, {}), _jsx("span", { className: "sr-only", children: "Toggle Sidebar" })] })));
});
SidebarTrigger.displayName = 'SidebarTrigger';
var SidebarRail = React.forwardRef(function SidebarRail(_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    var toggleSidebar = useSidebar().toggleSidebar;
    return (_jsx("button", __assign({ ref: ref, "data-sidebar": "rail", "aria-label": "Toggle Sidebar", tabIndex: -1, onClick: toggleSidebar, title: "Toggle Sidebar", className: cn('absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex', '[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize', '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize', 'group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full group-data-[collapsible=offcanvas]:hover:bg-sidebar', '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2', '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2', className) }, props)));
});
SidebarRail.displayName = 'SidebarRail';
var SidebarInset = React.forwardRef(function SidebarInset(_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx("main", __assign({ ref: ref, className: cn('relative flex w-full flex-1 flex-col bg-background', 'md:peer-data-[variant=inset]:m-2 md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow', className) }, props)));
});
SidebarInset.displayName = 'SidebarInset';
var SidebarInput = React.forwardRef(function SidebarInput(_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx(Input, __assign({ ref: ref, "data-sidebar": "input", className: cn('h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring', className) }, props)));
});
SidebarInput.displayName = 'SidebarInput';
var SidebarHeader = React.forwardRef(function SidebarHeader(_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx("div", __assign({ ref: ref, "data-sidebar": "header", className: cn('flex flex-col gap-2 p-2', className) }, props)));
});
SidebarHeader.displayName = 'SidebarHeader';
var SidebarFooter = React.forwardRef(function SidebarFooter(_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx("div", __assign({ ref: ref, "data-sidebar": "footer", className: cn('flex flex-col gap-2 p-2', className) }, props)));
});
SidebarFooter.displayName = 'SidebarFooter';
var SidebarSeparator = React.forwardRef(function SidebarSeparator(_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx(Separator, __assign({ ref: ref, "data-sidebar": "separator", className: cn('mx-2 w-auto bg-sidebar-border', className) }, props)));
});
SidebarSeparator.displayName = 'SidebarSeparator';
var SidebarContent = React.forwardRef(function SidebarContent(_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    var internalRef = React.useRef(null);
    var _b = React.useState(false), showScrollIndicator = _b[0], setShowScrollIndicator = _b[1];
    React.useEffect(function () {
        var el = internalRef.current;
        if (!el)
            return;
        var checkScroll = function () {
            var canScrollDown = el.scrollHeight - el.scrollTop - el.clientHeight > 20;
            setShowScrollIndicator(canScrollDown);
        };
        checkScroll();
        el.addEventListener('scroll', checkScroll);
        var ro = new ResizeObserver(checkScroll);
        ro.observe(el);
        return function () {
            el.removeEventListener('scroll', checkScroll);
            ro.disconnect();
        };
    }, []);
    return (_jsxs("div", { className: "relative flex min-h-0 flex-1 flex-col group-data-[collapsible=icon]:overflow-hidden", children: [_jsx("div", __assign({ ref: function (node) {
                    internalRef.current = node;
                    if (typeof ref === 'function')
                        ref(node);
                    else if (ref)
                        ref.current = node;
                }, "data-sidebar": "content", className: cn('flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden group-data-[collapsible=icon]:overflow-hidden scrollbar-none', className), style: {
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                } }, props)), showScrollIndicator && (_jsx("div", { className: "pointer-events-none absolute bottom-0 left-0 right-0 flex flex-col items-center pb-1 pt-6 bg-gradient-to-t from-sidebar to-transparent group-data-[collapsible=icon]:hidden", children: _jsx("svg", { className: "w-4 h-4 text-muted-foreground animate-bounce", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M19 14l-7 7m0 0l-7-7m7 7V3" }) }) }))] }));
});
SidebarContent.displayName = 'SidebarContent';
var SidebarGroup = React.forwardRef(function SidebarGroup(_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx("div", __assign({ ref: ref, "data-sidebar": "group", className: cn('relative flex w-full min-w-0 flex-col p-2', className) }, props)));
});
SidebarGroup.displayName = 'SidebarGroup';
var SidebarGroupLabel = React.forwardRef(function SidebarGroupLabel(_a, ref) {
    var className = _a.className, _b = _a.asChild, asChild = _b === void 0 ? false : _b, props = __rest(_a, ["className", "asChild"]);
    var Comp = asChild ? Slot : 'div';
    return (_jsx(Comp, __assign({ ref: ref, "data-sidebar": "group-label", className: cn('flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0', 'group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0', className) }, props)));
});
SidebarGroupLabel.displayName = 'SidebarGroupLabel';
var SidebarGroupAction = React.forwardRef(function SidebarGroupAction(_a, ref) {
    var className = _a.className, _b = _a.asChild, asChild = _b === void 0 ? false : _b, _c = _a.showOnHover, showOnHover = _c === void 0 ? false : _c, props = __rest(_a, ["className", "asChild", "showOnHover"]);
    var Comp = asChild ? Slot : 'button';
    return (_jsx(Comp, __assign({ ref: ref, "data-sidebar": "group-action", className: cn('absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0', 'after:absolute after:-inset-2 after:md:hidden', 'group-data-[collapsible=icon]:hidden', showOnHover &&
            'group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0', className) }, props)));
});
SidebarGroupAction.displayName = 'SidebarGroupAction';
var SidebarGroupContent = React.forwardRef(function SidebarGroupContent(_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx("div", __assign({ ref: ref, "data-sidebar": "group-content", className: cn('w-full text-sm', className) }, props)));
});
SidebarGroupContent.displayName = 'SidebarGroupContent';
var SidebarMenu = React.forwardRef(function SidebarMenu(_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx("ul", __assign({ ref: ref, "data-sidebar": "menu", className: cn('flex w-full min-w-0 flex-col gap-1', className) }, props)));
});
SidebarMenu.displayName = 'SidebarMenu';
var SidebarMenuItem = React.forwardRef(function SidebarMenuItem(_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx("li", __assign({ ref: ref, "data-sidebar": "menu-item", className: cn('group/menu-item relative', className) }, props)));
});
SidebarMenuItem.displayName = 'SidebarMenuItem';
var sidebarMenuButtonVariants = cva('peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0', {
    variants: {
        variant: {
            default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            outline: 'bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]',
        },
        size: {
            default: 'h-8 text-sm',
            sm: 'h-7 text-xs',
            lg: 'h-12 text-sm group-data-[collapsible=icon]:!p-0',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'default',
    },
});
var SidebarMenuButton = React.forwardRef(function SidebarMenuButton(_a, ref) {
    var _b = _a.asChild, asChild = _b === void 0 ? false : _b, _c = _a.isActive, isActive = _c === void 0 ? false : _c, _d = _a.variant, variant = _d === void 0 ? 'default' : _d, _e = _a.size, size = _e === void 0 ? 'default' : _e, tooltip = _a.tooltip, className = _a.className, props = __rest(_a, ["asChild", "isActive", "variant", "size", "tooltip", "className"]);
    var Comp = asChild ? Slot : 'button';
    var _f = useSidebar(), isMobile = _f.isMobile, state = _f.state;
    var button = (_jsx(Comp, __assign({ ref: ref, "data-sidebar": "menu-button", "data-size": size, "data-active": isActive, className: cn(sidebarMenuButtonVariants({ variant: variant, size: size }), className) }, props)));
    if (!tooltip) {
        return button;
    }
    if (typeof tooltip === 'string') {
        tooltip = {
            children: tooltip,
        };
    }
    return (_jsxs(Tooltip, { children: [_jsx(TooltipTrigger, { asChild: true, children: button }), _jsx(TooltipContent, __assign({ side: "right", align: "center", hidden: state !== 'collapsed' || isMobile }, tooltip))] }));
});
SidebarMenuButton.displayName = 'SidebarMenuButton';
var SidebarMenuAction = React.forwardRef(function SidebarMenuAction(_a, ref) {
    var className = _a.className, _b = _a.asChild, asChild = _b === void 0 ? false : _b, _c = _a.showOnHover, showOnHover = _c === void 0 ? false : _c, props = __rest(_a, ["className", "asChild", "showOnHover"]);
    var Comp = asChild ? Slot : 'button';
    return (_jsx(Comp, __assign({ ref: ref, "data-sidebar": "menu-action", className: cn('absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0', 'after:absolute after:-inset-2 after:md:hidden', 'peer-data-[size=sm]/menu-button:top-1', 'peer-data-[size=default]/menu-button:top-1.5', 'peer-data-[size=lg]/menu-button:top-2.5', 'group-data-[collapsible=icon]:hidden', showOnHover &&
            'group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0', className) }, props)));
});
SidebarMenuAction.displayName = 'SidebarMenuAction';
var SidebarMenuBadge = React.forwardRef(function SidebarMenuBadge(_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx("div", __assign({ ref: ref, "data-sidebar": "menu-badge", className: cn('pointer-events-none absolute right-1 flex h-5 min-w-5 select-none items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground', 'peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground', 'peer-data-[size=sm]/menu-button:top-1', 'peer-data-[size=default]/menu-button:top-1.5', 'peer-data-[size=lg]/menu-button:top-2.5', 'group-data-[collapsible=icon]:hidden', className) }, props)));
});
SidebarMenuBadge.displayName = 'SidebarMenuBadge';
var SidebarMenuSkeleton = React.forwardRef(function SidebarMenuSkeleton(_a, ref) {
    var className = _a.className, _b = _a.showIcon, showIcon = _b === void 0 ? false : _b, props = __rest(_a, ["className", "showIcon"]);
    var widthValue = React.useMemo(function () {
        return "".concat(Math.floor(Math.random() * 40) + 50, "%");
    }, []);
    return (_jsxs("div", __assign({ ref: ref, "data-sidebar": "menu-skeleton", className: cn('flex h-8 items-center gap-2 rounded-md px-2', className) }, props, { children: [showIcon && _jsx(Skeleton, { className: "size-4 rounded-md", "data-sidebar": "menu-skeleton-icon" }), _jsx(Skeleton, { className: "h-4 max-w-[--skeleton-width] flex-1", "data-sidebar": "menu-skeleton-text", style: {
                    '--skeleton-width': widthValue,
                } })] })));
});
SidebarMenuSkeleton.displayName = 'SidebarMenuSkeleton';
var SidebarMenuSub = React.forwardRef(function SidebarMenuSub(_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx("ul", __assign({ ref: ref, "data-sidebar": "menu-sub", className: cn('mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5', 'group-data-[collapsible=icon]:hidden', className) }, props)));
});
SidebarMenuSub.displayName = 'SidebarMenuSub';
var SidebarMenuSubItem = React.forwardRef(function SidebarMenuSubItem(props, ref) {
    return _jsx("li", __assign({ ref: ref }, props));
});
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';
var SidebarMenuSubButton = React.forwardRef(function SidebarMenuSubButton(_a, ref) {
    var _b = _a.asChild, asChild = _b === void 0 ? false : _b, _c = _a.size, size = _c === void 0 ? 'md' : _c, isActive = _a.isActive, className = _a.className, props = __rest(_a, ["asChild", "size", "isActive", "className"]);
    var Comp = asChild ? Slot : 'a';
    return (_jsx(Comp, __assign({ ref: ref, "data-sidebar": "menu-sub-button", "data-size": size, "data-active": isActive, className: cn('flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground outline-none ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground', 'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground', size === 'sm' && 'text-xs', size === 'md' && 'text-sm', 'group-data-[collapsible=icon]:hidden', className) }, props)));
});
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';
export { SidebarProvider, useSidebar, Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupAction, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarInput, SidebarInset, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuAction, SidebarMenuBadge, SidebarMenuSkeleton, SidebarMenuSub, SidebarMenuSubItem, SidebarMenuSubButton, SidebarRail, SidebarSeparator, SidebarTrigger, SidebarResizeHandle, };
